import { useMemo } from 'react';

import { CharacterVoicesFilters, CharacterVoiceType } from '@/types/schema';

import { useCharacterVoicesQuery } from './graphql/queries/generated/characterVoices';

export interface VideoLanguageOption {
  label: string;
  value: string;
}

function getLanguageLabel(languageCode: string): string {
  if (!languageCode) {
    return '';
  }

  const [language, region] = languageCode.split('-');

  if (!language || !region) {
    return languageCode;
  }

  const normalizedLanguage = language.toLowerCase();
  const normalizedRegion = region.toUpperCase();

  try {
    const languageDisplay = new Intl.DisplayNames(['en'], { type: 'language' });
    const regionDisplay = new Intl.DisplayNames(['en'], { type: 'region' });

    const languageName = languageDisplay.of(normalizedLanguage);
    const regionName = regionDisplay.of(normalizedRegion);

    if (languageName && regionName) {
      return `${languageName} (${regionName})`;
    }
  } catch {
    // Keep language code fallback when Intl.DisplayNames is unavailable.
  }

  return `${normalizedLanguage.toUpperCase()}-${normalizedRegion}`;
}

export function useVideoCharacterVoices({ filters }: { filters?: CharacterVoicesFilters }) {
  const [{ data, fetching, error }] = useCharacterVoicesQuery({
    variables: {
      filters,
    },
  });

  const voices = useMemo<CharacterVoiceType[]>(() => {
    return (data?.characterVoices?.edges?.map((edge) => edge?.node) || []) as CharacterVoiceType[];
  }, [data?.characterVoices?.edges]);

  const languages = useMemo<VideoLanguageOption[]>(() => {
    return Array.from(new Set(voices.map((voice) => voice.language)))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((language) => ({
        value: language,
        label: getLanguageLabel(language),
      }));
  }, [voices]);

  return {
    voices,
    languages,
    fetching,
    error,
    defaultLanguageValue: languages[0]?.value || '',
  };
}
